import type { PaidReturnMethod } from "./return-fee.lib";
import type { FinancialSummary } from "../types";

export type NetTotalsInput = {
  /** Return fee for the current basket, in cents. */
  fee: number;
  /** Bucket the fee is deducted from. */
  feeMethod: PaidReturnMethod | null;
  bonusTotal: number;
  bonusCurrency: string | null;
  /** Initial shipping given back, in cents. */
  initialShippingRefund: number;
  /** Bucket the initial shipping is added to (store credit wins, as in the legacy portal). */
  shippingMethod: PaidReturnMethod | null;
};

export type NetTotals = {
  netRefundTotal: number;
  netDiscountTotal: number;
  /** Bonus actually folded into the net, 0 when it is expressed in another currency. */
  appliedBonus: number;
  refundShipping: number;
  discountShipping: number;
};

function amountFor(
  method: PaidReturnMethod | null,
  bucket: PaidReturnMethod,
  amount: number,
): number {
  return method === bucket ? amount : 0;
}

/**
 * Applies the return fee, the store-credit bonus and the initial shipping refund on top
 * of the gross totals, mirroring the legacy receipt:
 *   refund   = articles − fee + shipping
 *   discount = articles − fee + shipping + bonus
 *
 * A `fixed_amount` bonus is configured in the shop currency, which is not always the
 * customer's: in that case it is reported but left out of the net rather than summed
 * across currencies.
 */
export function applyFeeAndBonus(
  summary: FinancialSummary,
  input: NetTotalsInput,
): NetTotals {
  const appliedBonus =
    input.bonusCurrency && input.bonusCurrency !== summary.currency
      ? 0
      : input.bonusTotal;

  const { shippingMethod, initialShippingRefund, feeMethod, fee } = input;
  const refundShipping = amountFor(shippingMethod, "refund", initialShippingRefund);
  const discountShipping = amountFor(shippingMethod, "discount", initialShippingRefund);
  const refundFee = amountFor(feeMethod, "refund", fee);
  const discountFee = amountFor(feeMethod, "discount", fee);

  return {
    netRefundTotal: Math.max(
      0,
      summary.refundTotal - refundFee + refundShipping,
    ),
    netDiscountTotal: Math.max(
      0,
      summary.discountTotal - discountFee + discountShipping + appliedBonus,
    ),
    appliedBonus,
    refundShipping,
    discountShipping,
  };
}
